import { Box, Flex, LoadingDots } from '@weareberyl/design-system'
import { Button, Modal, Switch, Timeline } from 'antd'
import { useEffect, useState } from 'react'
import {
  JourneyParkingPhotoQuery_journey_Journey_parking_photo_ParkingPhoto,
  useJourneyParkingPhotoLazyQuery,
  useJourneyParkingPhotoQuery,
  useJourneySummaryFragment,
} from '~/gql/generated/graphql'
import CardSection from '../shared/CardSection'
import { PARKING_DESCRIPTION, PHOTO_UPLOAD_EVENT } from '~/constants/domain'
import { useCurrentScheme } from '~/hooks'
import useFeatureFlags from '~/hooks/useFeatureFlags'
import { EndJourneyModal } from './actions'
import ParkingSuggestions from './ParkingSuggestions'

const JourneyPhotoModalContent = ({ journeyId }: { journeyId: string }) => {
  const [photoLog, setPhotoLog] = useState<
    JourneyParkingPhotoQuery_journey_Journey_parking_photo_ParkingPhoto[]
  >([])
  const [now, setNow] = useState(new Date())
  const { data, loading } = useJourneyParkingPhotoQuery({
    variables: { journeyId },
    pollInterval: 0,
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const photo = data?.journey?.parking_photo
    if (photo) {
      setPhotoLog(items =>
        items.some(item => item.updated_at === photo.updated_at)
          ? items
          : [...items, photo],
      )
    }
  }, [data?.journey?.parking_photo])

  const [featureFlags] = useFeatureFlags()

  if (loading) {
    return <LoadingDots />
  }

  const cardItems = [
    data?.journey?.parking_status
      ? {
          key: 'Parking',
          value: PARKING_DESCRIPTION[data.journey.parking_status],
        }
      : undefined,
    { key: 'Lock state', value: data?.journey?.vehicle_lock_state },
    { key: 'Frame ID', value: data?.journey?.vehicle.frame_id },
  ]

  return (
    <Flex style={{ gap: 10 }}>
      <Box flex={1}>
        <Flex mb={3}>
          <CardSection items={cardItems} />
        </Flex>
        <img
          src={data?.journey?.parking_photo.view.url}
          alt={`Parking photo journey:${data?.journey?.id}`}
        />
        {featureFlags.parking_controls && (
          <Box mt={4}>
            <Timeline mode="left">
              {photoLog.toReversed().map((item, index) => {
                const timestamp = new Date(item.updated_at)
                const diff = Math.floor((+now - +timestamp) / 1000)

                return (
                  <Timeline.Item
                    key={item.updated_at}
                    label={timestamp.toLocaleTimeString()}
                  >
                    {index === 0 && `${diff} seconds ago`}
                  </Timeline.Item>
                )
              })}
            </Timeline>
          </Box>
        )}
      </Box>

      {featureFlags.parking_controls && (
        <Box flex={1}>
          <EndJourneyModal journeyId={journeyId} />
          <ParkingSuggestions journeyId={journeyId} />
        </Box>
      )}
    </Flex>
  )
}

const JourneyPhotoModal = ({ journeyId }: { journeyId: string }) => {
  const [visible, setVisible] = useState(false)
  const hideModal = () => {
    setVisible(false)
  }
  const { data: journey } = useJourneySummaryFragment({ id: journeyId })
  const [{ push_parking_photo, parking_controls }] = useFeatureFlags()

  // When the auto-display switch is on and a photo.upload notification for THIS
  // journey arrives, the photo may be newer than what's cached, so refetch from
  // the network before opening. The refetch updates the normalised ParkingPhoto
  // entity, so the modal content (cache-first) reads the fresh photo.
  const [autoDisplay, setAutoDisplay] = useState(true)
  const [fetchPhoto] = useJourneyParkingPhotoLazyQuery({
    fetchPolicy: 'network-only',
  })

  const {
    schemeConfig: {
      config: { is_parking_photo_enabled },
    },
  } = useCurrentScheme()

  useEffect(() => {
    if (!push_parking_photo || !autoDisplay) {
      return
    }
    const handler = (event: Event) => {
      const { journeyId: eventJourneyId } =
        (event as CustomEvent<{ journeyId?: string }>).detail ?? {}
      if (eventJourneyId !== journeyId) {
        return
      }
      fetchPhoto({ variables: { journeyId } })
      setVisible(true)
    }
    window.addEventListener(PHOTO_UPLOAD_EVENT, handler)
    return () => window.removeEventListener(PHOTO_UPLOAD_EVENT, handler)
  }, [journeyId, autoDisplay, push_parking_photo, fetchPhoto])

  if (!is_parking_photo_enabled) {
    return null
  }

  return (
    <>
      <Modal
        open={visible}
        zIndex={1020}
        footer={null}
        onCancel={hideModal}
        width={parking_controls ? 1200 : 600}
        title={`Journey ${journeyId}`}
      >
        {visible && <JourneyPhotoModalContent journeyId={journeyId} />}
      </Modal>
      <Box mt={2} alignItems="flex-start">
        <Button
          onClick={() => setVisible(true)}
          disabled={!journey?.parking_photo?.updated_at}
        >
          View parking photo
        </Button>
        {push_parking_photo && (
          <Flex mt={2} alignItems="center" style={{ gap: 8 }}>
            <Switch
              size="small"
              checked={autoDisplay}
              onChange={setAutoDisplay}
            />
            <span>Auto-show new photo</span>
          </Flex>
        )}
      </Box>
    </>
  )
}

export default JourneyPhotoModal
